'use client';

import type { Dispatch } from 'react';
import type { ZoneForm, ZoneFormAction } from './zoneForm';
import { ZONE_COLORS, resolveZoneColor } from './zoneColors';

interface ZoneDrawFormProps {
  drawMode: 'circle' | 'polygon';
  drawPoints: [number, number][];
  polygonClosed: boolean;
  onClosePolygon: () => void;
  circleCenter: { lat: number; lng: number } | null;
  onSetCircleCenter: (lat: number, lng: number) => void;
  circleRadius: number;
  onCircleRadiusChange: (r: number) => void;
  form: ZoneForm;
  dispatchForm: Dispatch<ZoneFormAction>;
  saving: boolean;
  canSave: boolean;
  onCancel: () => void;
  onSave: () => void;
}

export function ZoneDrawForm({
  drawMode,
  drawPoints,
  polygonClosed,
  onClosePolygon,
  circleCenter,
  onSetCircleCenter,
  circleRadius,
  onCircleRadiusChange,
  form,
  dispatchForm,
  saving,
  canSave,
  onCancel,
  onSave,
}: ZoneDrawFormProps) {
  const activeColor = resolveZoneColor(form.type, form.color);

  return (
    <div className="border-t divider p-3 space-y-2.5 bg-black/[0.02] dark:bg-white/[0.02] shrink-0">
      <div className="text-[10px] text-primary uppercase tracking-wider">
        {drawMode === 'circle'
          ? 'Click map to set center'
          : `Click map to add points · ${drawPoints.length} pt${drawPoints.length !== 1 ? 's' : ''}${polygonClosed ? ' · CLOSED' : ''}`}
      </div>

      <input
        type="text"
        placeholder="Zone name"
        aria-label="Zone name"
        value={form.name}
        onChange={(e) => dispatchForm({ kind: 'patch', patch: { name: e.target.value } })}
        className="w-full h-7 px-2 text-xs bg-background border border-input rounded text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50"
      />

      <select
        value={form.type}
        aria-label="Zone type"
        onChange={(e) => dispatchForm({ kind: 'patch', patch: { type: e.target.value } })}
        className="w-full h-7 px-2 text-xs bg-background border border-input rounded text-foreground focus:outline-none focus:border-primary/50 appearance-none"
      >
        <option value="operational">Operational</option>
        <option value="restricted">Restricted</option>
        <option value="warning">Warning</option>
        <option value="jamming">GPS Jamming</option>
      </select>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
            Color
          </span>
          {form.color === null && (
            <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wider">
              Type default
            </span>
          )}
        </div>
        <div
          role="radiogroup"
          aria-label="Zone color"
          className="grid grid-cols-8 gap-1"
        >
          {ZONE_COLORS.map((option) => {
            const selected = option.hex === activeColor;
            return (
              <label key={option.hex} title={option.label} className="block cursor-pointer">
                <input
                  type="radio"
                  name="zone-color"
                  checked={selected}
                  onChange={() => dispatchForm({ kind: 'patch', patch: { color: option.hex } })}
                  // `onClick` as well as `onChange`, because the swatch matching
                  // the TYPE DEFAULT already renders checked while `form.color`
                  // is still null. React fires onChange for a radio only when
                  // the tracked checked value changes, so clicking that one was
                  // a no-op: the operator saw nothing move, believed the pick
                  // had taken, then changed the zone type — and the colour
                  // silently followed the new type instead of staying where
                  // they put it. An explicit pick must register even when it
                  // equals the current default.
                  onClick={() => dispatchForm({ kind: 'patch', patch: { color: option.hex } })}
                  className="peer sr-only"
                />
                <span className="sr-only">{option.label}</span>
                <span
                  aria-hidden="true"
                  style={{ backgroundColor: option.hex }}
                  className={`block h-6 rounded transition-shadow peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-background ${
                    selected
                      ? 'ring-2 ring-foreground ring-offset-1 ring-offset-background'
                      : 'ring-1 ring-inset ring-black/20 dark:ring-white/20 hover:ring-foreground/50'
                  }`}
                />
              </label>
            );
          })}
        </div>
      </div>

      {drawMode === 'circle' && (
        <div className="space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <input
              type="number"
              step="any"
              placeholder="Lat"
              aria-label="Circle center latitude"
              value={circleCenter?.lat.toFixed(6) ?? ''}
              onChange={(e) => {
                const lat = parseFloat(e.target.value);
                if (Number.isFinite(lat))
                  onSetCircleCenter(lat, circleCenter?.lng ?? 0);
              }}
              className="h-6 px-2 text-[10px] bg-background border border-input rounded text-foreground font-mono tabular-nums focus:outline-none focus:border-primary/50"
            />
            <input
              type="number"
              step="any"
              placeholder="Lng"
              aria-label="Circle center longitude"
              value={circleCenter?.lng.toFixed(6) ?? ''}
              onChange={(e) => {
                const lng = parseFloat(e.target.value);
                if (Number.isFinite(lng))
                  onSetCircleCenter(circleCenter?.lat ?? 0, lng);
              }}
              className="h-6 px-2 text-[10px] bg-background border border-input rounded text-foreground font-mono tabular-nums focus:outline-none focus:border-primary/50"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground shrink-0">R(m)</span>
            <input
              type="number"
              aria-label="Circle radius in meters"
              value={circleRadius}
              onChange={(e) =>
                onCircleRadiusChange(Math.max(0, parseFloat(e.target.value) || 0))
              }
              className="flex-1 h-6 px-2 text-[10px] bg-background border border-input rounded text-foreground font-mono tabular-nums focus:outline-none focus:border-primary/50"
            />
          </div>
        </div>
      )}

      {drawMode === 'polygon' && drawPoints.length >= 3 && !polygonClosed && (
        <button
          type="button"
          onClick={onClosePolygon}
          className="w-full h-7 text-xs bg-status-green/15 text-status-green-fg hover:bg-status-green/25 border border-status-green/30 rounded transition-colors"
        >
          CLOSE POLYGON
        </button>
      )}

      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-muted-foreground shrink-0">ALT(m)</span>
        <input
          type="number"
          placeholder="Max"
          aria-label="Maximum altitude in meters"
          value={form.maxAlt}
          onChange={(e) => dispatchForm({ kind: 'patch', patch: { maxAlt: e.target.value } })}
          className="flex-1 h-6 px-2 text-[10px] bg-background border border-input rounded text-foreground font-mono tabular-nums focus:outline-none focus:border-primary/50"
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.alertEnabled}
          onChange={(e) => dispatchForm({ kind: 'patch', patch: { alertEnabled: e.target.checked } })}
          className="size-3.5 rounded border-input bg-background accent-primary"
        />
        <span className="text-[10px] text-muted-foreground">Trigger alerts when drones enter/exit</span>
      </label>

      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 h-7 text-xs text-muted-foreground hover:text-foreground border border-input rounded transition-colors"
        >
          CANCEL
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !canSave}
          className="flex-1 h-7 text-xs bg-button-primary hover:bg-button-primary-hover text-primary-foreground rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'SAVING...' : 'SAVE'}
        </button>
      </div>
    </div>
  );
}
